import { UserLearningAssignments } from "../../components/learning/UserLearningAssignments";
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Mail,
  Power,
  PowerOff,
  Pencil,
  Save,
  Trash2,
} from "lucide-react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { userApi } from "../../backend/api/users";
import type {
  InvitationResult,
  UserDependencySummary,
  UserRecord,
  UserRole,
} from "../../backend/api/users";
import { Alert, AlertDescription, AlertTitle } from "../../components/ui/alert";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import TimezoneSelect from "../global/TimezoneSelect";
import { InvitationNotice } from "./InvitationNotice";
import { UserAccountStatus } from "../../components/domain-badges";

const ROLES: UserRole[] = ["Client", "Agent", "Admin", "Owner"];

type ProfileForm = {
  firstname: string;
  lastname: string;
  contactnumber: string;
  ianatimezonecode: string;
};

/** Where the business runs, used when a record carries no zone of its own. */
const DEFAULT_TIMEZONE = "Australia/Brisbane|UTC+10:00";

const profileFormFor = (user: UserRecord): ProfileForm => ({
  firstname: user.firstname,
  lastname: user.lastname,
  contactnumber: user.contactnumber || "",
  ianatimezonecode: user.ianatimezonecode || DEFAULT_TIMEZONE,
});

const dependencyRows = (summary?: UserDependencySummary | null) =>
  [
    ["client projects", summary?.customer_projects || 0],
    ["staff projects", summary?.agent_projects || 0],
    ["project assignment history", summary?.participant_assignments || 0],
    ["client documents", summary?.customer_documents || 0],
    ["uploaded documents", summary?.uploaded_documents || 0],
    ["comments", summary?.authored_comments || 0],
  ] as const;

const hasDependencies = (summary?: UserDependencySummary | null) =>
  dependencyRows(summary).some(([, count]) => count > 0);

const formatDateTime = (value?: string | null) => {
  if (!value) return "Never";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString("en-AU", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

/** A user's own page, so actions like invitations have room to explain themselves. */
export default function UserDetail() {
  const { userId = "" } = useParams();
  const navigate = useNavigate();

  const [user, setUser] = useState<UserRecord | null>(null);
  const [role, setRole] = useState<UserRole>("Agent");
  const [profile, setProfile] = useState<ProfileForm | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [invitation, setInvitation] = useState<InvitationResult | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [workEmail, setWorkEmail] = useState("");
  const [elevating, setElevating] = useState(false);
  const [dependencies, setDependencies] =
    useState<UserDependencySummary | null>(null);
  const [dependencyError, setDependencyError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error: loadError } = await userApi.get(userId);
    setLoading(false);

    if (loadError) {
      setError(loadError.message || "Could not load this user.");
      return;
    }

    setError("");
    setUser(data);
    if (data) {
      setRole(data.role);
      setProfile(profileFormFor(data));
      setWorkEmail(data.work_email || "");
    }
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  const disabled = Boolean(user?.archived_at) || user?.account_enabled === false;

  const profileChanged =
    Boolean(user && profile) &&
    (profile!.firstname !== user!.firstname ||
      profile!.lastname !== user!.lastname ||
      profile!.contactnumber !== (user!.contactnumber || "") ||
      profile!.ianatimezonecode !== (user!.ianatimezonecode || ""));

  const saveProfile = async () => {
    if (!user || !profile) return;
    setBusy("profile");
    setError("");
    setNotice("");
    const { error: saveError } = await userApi.update(user.id, {
      expected_version: user.version,
      firstname: profile.firstname,
      lastname: profile.lastname,
      contactnumber: profile.contactnumber || null,
      ianatimezonecode: profile.ianatimezonecode || null,
    });
    setBusy("");

    if (saveError) {
      setError(saveError.message || "Could not save the profile.");
      return;
    }

    setNotice("Profile saved.");
    await load();
  };

  const saveRole = async () => {
    if (!user) return;

    // An admin signs in through the organisation's identity provider, so the
    // work address has to be collected before the role is granted rather than
    // left to be filled in afterwards.
    if (["Admin", "Owner"].includes(role) && !["Admin", "Owner"].includes(user.role) && !user.work_email) {
      setWorkEmail("");
      setElevating(true);
      return;
    }

    setBusy("role");
    setError("");
    setNotice("");
    const { error: saveError } = await userApi.update(user.id, {
      expected_version: user.version,
      role,
    });
    setBusy("");

    if (saveError) {
      setError(saveError.message || "Could not change the role.");
      return;
    }

    setNotice(
      role === user.role
        ? "Role unchanged."
        : `Role changed to ${role}.${
            ["Admin", "Owner"].includes(user.role) && !["Admin", "Owner"].includes(role) ? " Their work email has been released." : ""
          }`,
    );
    await load();
  };

  const elevateToStaffAdmin = async () => {
    if (!user) return;
    setBusy("role");
    setError("");
    const { error: saveError } = await userApi.update(user.id, {
      expected_version: user.version,
      role,
      work_email: workEmail.trim(),
    });
    setBusy("");

    if (saveError) {
      setError(saveError.message || "Could not elevate this user.");
      return;
    }

    setElevating(false);
    setNotice(`${user.email} is now an ${role}, with work email ${workEmail.trim()}.`);
    await load();
  };

  const saveWorkEmail = async () => {
    if (!user) return;
    setBusy("work-email");
    setError("");
    setNotice("");
    const { error: saveError } = await userApi.update(user.id, {
      expected_version: user.version,
      work_email: workEmail.trim(),
    });
    setBusy("");

    if (saveError) {
      setError(saveError.message || "Could not save the work email.");
      return;
    }

    setElevating(false);
    setNotice("Work email updated.");
    await load();
  };

  const setStatus = async (nextDisabled: boolean) => {
    if (!user) return;

    setBusy("status");
    setError("");
    setNotice("");

    // Disabling archives. Enabling reverses whichever way access was withdrawn:
    // an archived account is restored, one merely switched off is switched back on.
    const result = nextDisabled
      ? await userApi.archive(user.id, {
          expected_version: user.version,
          reason: "Disabled by an administrator",
        })
      : user.archived_at
        ? await userApi.restore(user.id, user.version)
        : await userApi.update(user.id, {
            expected_version: user.version,
            account_enabled: true,
          });
    setBusy("");

    if (result.error) {
      setError(result.error.message || "Could not change the status.");
      return;
    }

    setNotice(nextDisabled ? "User disabled." : "User enabled.");
    await load();
  };

  const invite = async () => {
    if (!user) return;
    setBusy("invite");
    setError("");
    setNotice("");
    setInvitation(null);
    const { data, error: inviteError } = await userApi.invite(user.id);
    setBusy("");

    if (inviteError) {
      setError(inviteError.message || "Could not issue an invitation.");
      return;
    }

    setInvitation(data);
    await load();
  };

  /** Deletion is only offered once disabled, and only when nothing references the account. */
  const openDelete = async () => {
    if (!user) return;
    setConfirmingDelete(true);
    setDependencies(null);
    setDependencyError("");
    const { data, error: summaryError } = await userApi.dependencies(user.id);
    if (summaryError) {
      setDependencyError(
        summaryError.message || "Could not check retained history.",
      );
      return;
    }
    setDependencies(data);
  };

  const confirmDelete = async () => {
    if (!user) return;
    setBusy("delete");
    setError("");
    const { error: deleteError } = await userApi.permanentlyDelete(
      user.id,
      user.version,
    );
    setBusy("");

    if (deleteError) {
      setError(deleteError.message || "Could not delete this user.");
      setConfirmingDelete(false);
      return;
    }

    navigate("/user-management");
  };

  const fullName = [user?.firstname, user?.lastname].filter(Boolean).join(" ");
  const deleteBlocked = !dependencies || hasDependencies(dependencies);

  return (
    <main className="mx-auto flex w-full max-w-[1500px] flex-col gap-5 p-4 sm:p-6">
      <Link
        to="/user-management"
        className="inline-flex w-fit items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        Users
      </Link>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold">
            {loading ? "Loading..." : fullName || user?.email || "User"}
          </h1>
          <p className="text-sm text-muted-foreground">{user?.email}</p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            onClick={saveProfile}
            disabled={busy !== "" || disabled || !profileChanged}
          >
            <Save className="h-4 w-4" />
            {busy === "profile" ? "Saving..." : "Save profile"}
          </Button>

          <Button variant="outline" onClick={invite} disabled={busy !== "" || disabled}>
            <Mail className="h-4 w-4" />
            {busy === "invite" ? "Preparing..." : "Invite user"}
          </Button>

          {disabled ? (
            <>
              <Button variant="outline" onClick={() => setStatus(false)} disabled={busy !== ""}>
                <Power className="h-4 w-4" />
                {busy === "status" ? "Enabling..." : "Enable"}
              </Button>
              {user?.archived_at && (
                <Button
                  variant="outline"
                  className="text-destructive hover:text-destructive"
                  onClick={openDelete}
                  disabled={busy !== ""}
                >
                  <Trash2 className="h-4 w-4" />
                  Delete
                </Button>
              )}
            </>
          ) : (
            <Button
              variant="outline"
              className="text-destructive hover:text-destructive"
              onClick={() => setStatus(true)}
              disabled={busy !== ""}
            >
              <PowerOff className="h-4 w-4" />
              {busy === "status" ? "Disabling..." : "Disable"}
            </Button>
          )}
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertTitle>Something went wrong</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {notice && (
        <Alert>
          <AlertTitle>Done</AlertTitle>
          <AlertDescription>{notice}</AlertDescription>
        </Alert>
      )}

      {invitation && <InvitationNotice key={invitation.invitation_url || "delivered"} invitation={invitation} />}

      <div className="grid items-stretch gap-5 xl:grid-cols-[1.35fr_0.85fr]">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Profile</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-2">
              <Label htmlFor="user-firstname">First name</Label>
              <Input
                id="user-firstname"
                value={profile?.firstname || ""}
                disabled={disabled || loading}
                onChange={(event) =>
                  setProfile((value) =>
                    value ? { ...value, firstname: event.target.value } : value,
                  )
                }
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="user-lastname">Last name</Label>
              <Input
                id="user-lastname"
                value={profile?.lastname || ""}
                disabled={disabled || loading}
                onChange={(event) =>
                  setProfile((value) =>
                    value ? { ...value, lastname: event.target.value } : value,
                  )
                }
              />
            </div>
            <div className="grid gap-2">
              <Label>Contact number</Label>
              <PhoneInput
                country="au"
                value={profile?.contactnumber || ""}
                disabled={disabled || loading}
                onChange={(value, _country, _event, formatted) =>
                  setProfile((current) =>
                    current
                      ? { ...current, contactnumber: formatted || value }
                      : current,
                  )
                }
                alwaysDefaultMask={false}
                containerClass="!w-full"
                inputClass="!h-10 !w-full !rounded-md !border !border-input !bg-background !pl-12 !text-sm"
                buttonClass="!rounded-l-md !border-input !bg-background"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="user-timezone">Time zone</Label>
              <TimezoneSelect
                id="user-timezone"
                name="ianatimezonecode"
                value={profile?.ianatimezonecode || ""}
                onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
                  setProfile((value) =>
                    value
                      ? { ...value, ianatimezonecode: event.target.value }
                      : value,
                  )
                }
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="user-role">Role</Label>
              <Select value={role} onValueChange={(value) => setRole(value as UserRole)}>
                <SelectTrigger id="user-role" disabled={disabled || loading}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map((value) => (
                    <SelectItem key={value} value={value}>
                      {value}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {role !== user?.role && (
                <Button
                  size="sm"
                  className="w-fit"
                  onClick={saveRole}
                  disabled={busy !== "" || disabled}
                >
                  {busy === "role" ? "Saving..." : `Change to ${role}`}
                </Button>
              )}
            </div>

            {["Admin", "Owner"].includes(user?.role || "") && (
              <div className="grid gap-2">
                <Label>Work email</Label>
                <div className="flex items-center gap-3">
                  <span className="text-sm">{user.work_email || "Not set"}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={disabled || loading}
                    onClick={() => {
                      setWorkEmail(user.work_email || "");
                      setElevating(true);
                    }}
                  >
                    <Pencil className="h-4 w-4" />
                    Edit
                  </Button>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {user && user.role !== "Client" && !disabled && <UserLearningAssignments key={user.id} userId={user.id} />}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Account</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4">
          <Field label="Status">
            {user ? <UserAccountStatus user={user} /> : "Loading..."}
          </Field>
          <Field label="Signs in with">{user?.email || "Not set"}</Field>
          <Field label="Last sign in">{formatDateTime(user?.last_sign_in_at)}</Field>
          <Field label="Created">{formatDateTime(user?.accountcreationdate)}</Field>
        </CardContent>
      </Card>
      </div>


      <Dialog open={elevating} onOpenChange={(open) => !open && setElevating(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {["Admin", "Owner"].includes(user?.role || "")
                ? "Work email"
                : `Make ${fullName || user?.email} a ${role}`}
            </DialogTitle>
            <DialogDescription>
              Admins and Owners have an organisation work email for staff identity setup.
              It is released automatically when they move to Agent or Client.
              Owner includes Admin access and manages Xero. After the first Owner,
              only an Owner can grant or change Owner access.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-2">
            <Label htmlFor="elevate-work-email">Work email</Label>
            <Input
              id="elevate-work-email"
              type="email"
              placeholder="name@claymorethistle.com"
              value={workEmail}
              onChange={(event) => setWorkEmail(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && workEmail.trim()) {
                  if (["Admin", "Owner"].includes(user?.role || "")) saveWorkEmail();
                  else elevateToStaffAdmin();
                }
              }}
            />
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              disabled={busy !== ""}
              onClick={() => {
                setElevating(false);
                setRole(user?.role || "Agent");
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={["Admin", "Owner"].includes(user?.role || "") ? saveWorkEmail : elevateToStaffAdmin}
              disabled={busy !== "" || !workEmail.trim()}
            >
              {busy !== ""
                ? "Saving..."
                : ["Admin", "Owner"].includes(user?.role || "")
                  ? "Save"
                  : `Make ${role}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={confirmingDelete}
        onOpenChange={(open) => {
          if (!open && !busy) setConfirmingDelete(false);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Permanently delete user</DialogTitle>
            <DialogDescription>
              This removes {fullName || user?.email} for good. It is only allowed
              when no history references the account.
            </DialogDescription>
          </DialogHeader>

          {dependencyError && (
            <div className="text-sm text-destructive" role="alert">
              {dependencyError}
            </div>
          )}

          {!dependencies && !dependencyError && (
            <div className="text-sm text-muted-foreground" role="status">
              Checking retained history...
            </div>
          )}

          {dependencies && (
            <div className="rounded-md border p-3 text-sm">
              <div className="mb-2 font-medium">Retained history</div>
              <ul className="space-y-1 text-muted-foreground">
                {dependencyRows(dependencies).map(([label, count]) => (
                  <li key={label} className="flex justify-between gap-4">
                    <span>{label}</span>
                    <span>{count}</span>
                  </li>
                ))}
              </ul>
              {hasDependencies(dependencies) && (
                <div className="mt-3 text-destructive">
                  Deletion is blocked. Leave this account disabled so its history
                  stays intact.
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmingDelete(false)}
              disabled={busy === "delete"}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={deleteBlocked || busy === "delete"}
            >
              {busy === "delete" ? "Deleting..." : "Delete permanently"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
}

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div>
    <dt className="text-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
    <dd className="mt-1 text-sm">{children}</dd>
  </div>
);
